import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { itineraryApi } from "@/lib/api";
import type { ItineraryResponse } from "@/lib/types";
import TripTimeline from "@/components/trips/unified/TripTimeline";

/**
 * The trip as a document you can take with you.
 *
 * This page used to be a SECOND itinerary renderer, with its own flight rows,
 * its own item rows and its own idea of a day — which meant every improvement to
 * the trip page had to be made twice, and never was. It now renders the same
 * `TripTimeline` the trip page renders, in `printMode`: every day open, no
 * controls, no fare panels. One renderer, so the paper cannot drift from the
 * screen.
 *
 * Page breaks, margins and the hiding of screen-only chrome live in the
 * `@media print` block in index.css.
 */
export default function ItineraryPrint() {
  const { id } = useParams<{ id: string }>();
  const printed = useRef(false);

  const { data } = useQuery<ItineraryResponse>({
    queryKey: ["itinerary", id],
    queryFn: () => itineraryApi.get(id!),
    enabled: !!id,
  });

  // Open the print dialog once, and only after the days are actually on the
  // page — printing a skeleton is worse than not printing at all.
  useEffect(() => {
    if (printed.current || !data || data.days.length === 0) return;
    printed.current = true;
    const t = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 print:max-w-none print:px-0 print:py-0">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link
          to={"/trips/" + id}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to trip
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-[hsl(var(--foreground))] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-90 motion-reduce:transition-none"
        >
          <Printer className="h-3.5 w-3.5" aria-hidden="true" />
          Print / Save PDF
        </button>
      </div>

      <header className="mb-4 border-b border-border pb-3">
        <h1 className="text-xl font-semibold tracking-tight">
          {data?.trip.name ?? "Itinerary"}
        </h1>
        {data?.trip.description && (
          <p className="mt-1 text-xs leading-snug text-muted-foreground">
            {data.trip.description}
          </p>
        )}
      </header>

      {id && <TripTimeline tripId={id} printMode />}
    </div>
  );
}
